/**
 * Image Upload Service
 * Handles client-side image upload operations
 */

import { getAuth } from 'firebase/auth';

/**
 * Upload category type
 */
export type UploadCategory = 'aircraft' | 'marketplace' | 'profile' | 'site' | 'general';

/**
 * Upload options
 */
export interface UploadOptions {
  /** Upload category - determines storage location */
  category?: UploadCategory;
  /** Optional subdirectory within category */
  subdir?: string;
  /** Progress callback (0-100) */
  onProgress?: (progress: number) => void;
}

/**
 * Upload an image file to the server (requires authentication)
 * @param file - The image file to upload
 * @param options - Upload options including category, subdir, and progress callback
 * @returns The public URL of the uploaded image
 */
export async function uploadImage(
  file: File,
  options?: UploadOptions | ((progress: number) => void)
): Promise<string> {
  // Handle legacy signature where second param is just onProgress
  const opts: UploadOptions = typeof options === 'function'
    ? { onProgress: options }
    : options || {};

  const { category = 'general', subdir, onProgress } = opts;
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select an image file');
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('Image must be smaller than 10MB');
    }

    // Get the current user's auth token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('You must be logged in to upload images');
    }

    const token = await user.getIdToken();

    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (subdir) {
      formData.append('subdir', subdir);
    }

    // Upload with progress tracking
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && response.url) {
              // Use the URL provided by the server
              resolve(response.url);
            } else {
              reject(new Error(response.error || 'Upload failed - no URL in response'));
            }
          } catch (error) {
            console.error('Failed to parse upload response:', xhr.responseText);
            reject(new Error('Failed to parse server response'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error || `Upload failed with status ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Send request with auth token
      xhr.open('POST', '/api/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
}

/**
 * Convert a File or Blob to a data URL for preview
 */
export function fileToDataURL(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Validate and potentially resize an image file
 */
export async function prepareImageForUpload(
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 800,
  quality: number = 0.9
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Set canvas size and draw image
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Failed to resize image'));
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}
